import { useCallback } from 'react';

// Map of route paths to their lazy-loaded component imports
const routeMap = {
  '/dashboard': () => import('@/pages/Dashboard'),
  '/assessments': () => import('@/pages/Assessments'),
  '/blogs': () => import('@/pages/Blogs'),
  '/manage-assessments': () => import('@/pages/AdminAssessments'),
  '/results': () => import('@/pages/Results'),
  '/profile': () => import('@/pages/UserProfile'),
  '/upgrade': () => import('@/pages/Upgrade'),
  '/help': () => import('@/pages/Help'),
  '/contact': () => import('@/pages/Contact'),
  '/admin/users': () => import('@/pages/ManageUsers'),
  '/admin/feedback': () => import('@/pages/UserFeedback'),
  '/admin/pricing': () => import('@/pages/ManageCountryPricing'),
} as const;

type RoutePath = keyof typeof routeMap;

/**
 * Hook for prefetching route chunks to enable near-instant navigation
 * Uses requestIdleCallback to avoid blocking the main thread
 */
export const useRoutePrefetch = () => {
  const prefetchRoute = useCallback((path: RoutePath) => {
    // Don't prefetch on mobile or if user has data saver enabled
    if (typeof window === 'undefined') return;
    
    const connection = (navigator as any).connection;
    if (connection?.saveData) return;
    
    const importFn = routeMap[path];
    if (!importFn) return;

    // Prefetch the route chunk
    importFn().catch(() => {
      // Silently fail - route will load normally when navigated to
    });
  }, []);

  const prefetchRoutes = useCallback((paths: RoutePath[]) => {
    if (typeof window === 'undefined') return;
    
    const requestIdleCallback = (window as any).requestIdleCallback || 
      ((cb: IdleRequestCallback) => setTimeout(cb, 1));

    requestIdleCallback(() => {
      paths.forEach((path, index) => {
        // Stagger prefetches to avoid network congestion
        setTimeout(() => prefetchRoute(path), index * 100);
      });
    }, { timeout: 2000 });
  }, [prefetchRoute]);

  return { prefetchRoute, prefetchRoutes };
};
